/**
 * Why one person is on the "needs attention" list.
 *
 * The list used to show a name and a percentage, and at the start of a
 * training cycle every row reads 0% — eight people who look identical and are
 * not. One has opened nothing, another is halfway through four courses with a
 * deadline on Friday. Same number, opposite conversations.
 *
 * Deliberately does not mention overdue: that already has a red badge on the
 * row, and saying it twice makes the badge worth less.
 */
export interface MemberWorkload {
  assigned: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  dueSoon: number;
}

export function describeWorkload(member: MemberWorkload): string {
  /**
   * Somebody flagged with nothing assigned.
   *
   * Reachable: the list also catches people at 0% who were never given
   * anything, and "0 not started" would be a strange way to say that.
   */
  if (member.assigned === 0) return 'Nothing assigned yet';

  // The whole workload untouched is worth its own sentence — it is the single
  // most common reason to be on this list, and the easiest to fix.
  if (member.notStarted === member.assigned) {
    return member.assigned === 1
      ? 'Has not opened their assigned course'
      : `Has not opened any of their ${member.assigned} courses`;
  }

  const parts: string[] = [];
  if (member.notStarted > 0) parts.push(`${member.notStarted} not started`);
  if (member.inProgress > 0) parts.push(`${member.inProgress} in progress`);
  if (member.dueSoon > 0) parts.push(`${member.dueSoon} due this week`);

  // Everything is finished, or the only thing outstanding is already overdue —
  // which the badge covers. Fall back to the plain ratio rather than an empty
  // line, so the row never collapses.
  if (parts.length === 0) return `${member.completed} of ${member.assigned} completed`;

  return parts.join(' · ');
}
